import { readonly, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import {
  httpLogPlugin,
  appRunUuidStorage,
  isVoucher,
  logService,
  ScanService
} from '@nsftx/seven-client-retail-utils'
import {
  deviceManagementService,
  deviceManagementPrintService,
  deviceManagementAcceptorsService,
  type DmPrintConfig,
  useDeviceManagementStore,
  setDeviceAccessToken
} from '@nsftx/seven-client-retail-vue-dm'
import { useSpinnerStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import {
  cmsService,
  useSevenIgamingPlatformConfig,
  useTokenPayloadStore
} from '@nsftx/seven-client-retail-vue-sgp'
import {
  useDmCoreSpaceOperatorAuthStore,
  useDmCoreSpaceOperatorStore
} from '@nsftx/seven-client-retail-vue-dm-core'
import errorParser from '@/utils/errorParser'
import { useSettingsStore } from '@/stores/settings/settingsStore'
import { useCmsConfigStore } from '@/stores/cms/cmsConfigStore'
import router from '@/router'
import AppBootstrapError from '@/classes/AppBootstrapError'
import printService from '@/modules/print/services/printService'
import { applyDeviceSettingsFromBootstrap } from '@/composables/useDeviceSettingsBootstrap/useDeviceSettingsBootstrap'
import { bootGames } from '@/modules/games/gameBooting'

const LOG_PREFIX = '[useAppBootstrap]'

const isAppReady = ref(false)

export const useAppBootstrap = () => {
  const scan = ScanService()
  const { showSpinner, closeSpinner } = useSpinnerStore()
  const settingsStore = useSettingsStore()
  const tokenPayloadStore = useTokenPayloadStore()
  const sevenIgamingPlatformConfig = useSevenIgamingPlatformConfig()
  const { operatorData, isOperatorLoggedIn, operatorDisplayName } = storeToRefs(
    useDmCoreSpaceOperatorStore()
  )
  const { getOperatorData, startOperatorAccessTokenAutoRefresh } = useDmCoreSpaceOperatorAuthStore()
  const { appData } = storeToRefs(useDeviceManagementStore())

  const initTokenServices = async () => {
    try {
      printService.initPrintService()
    } catch (error) {
      logService.error(`${LOG_PREFIX} Failed to initialize client print service.`, {
        code: 'SBO_BOOT_CLIENT_PRINT_SERVICE_INIT_ERROR',
        ...errorParser.parseUpstream(error)
      })
    }

    try {
      printService.addOperatorDataToGlobals(operatorDisplayName.value!)
    } catch (error) {
      logService.error(`${LOG_PREFIX} Failed to add operator data to print globals.`, {
        code: 'SBO_BOOT_OPERATOR_PRINT_GLOBALS_ERROR',
        ...errorParser.parseUpstream(error)
      })
    }
  }

  const initBackOfficeAcceptors = () => {
    logService.info(`${LOG_PREFIX} Stopping acceptors on back office visit.`, {
      code: 'SBO_STOP_ACCEPTORS_ON_INIT'
    })

    deviceManagementAcceptorsService.init({ startAcceptorsOnInit: false }).catch((error) => {
      logService.error(`${LOG_PREFIX} Failed to stop acceptors on back office init.`, {
        code: 'SBO_STOP_ACCEPTORS_ON_INIT_ERROR',
        ...errorParser.parseUpstream(error)
      })
    })

    deviceManagementAcceptorsService.detectInitialAcceptorsState()
  }

  const addScanListener = () => {
    scan.listenForScannerEvents((res) => {
      logService.info(`${LOG_PREFIX} Scanner event received`, {
        code: 'SBO_SCAN_EVENT_RECEIVED',
        details: { isVoucher: isVoucher(res.data.code) }
      })
      // TODO: route to ticket/slip check when Tickets module is implemented
    })
  }

  const startApp = async () => {
    showSpinner()

    try {
      const appRunUuid = appRunUuidStorage.getAppRunUuid()
      const printConfig: DmPrintConfig = {
        persist: { prefix: 'sbo', storage: 'localStorage' }
      }

      if (!appRunUuid) {
        const errorMessage = 'appRunUuid is not defined.'
        const errorCode = 'SBO_BOOT_APP_RUN_UUID_ERROR'

        logService.error(`${LOG_PREFIX} ${errorMessage}`, {
          code: errorCode
        })

        throw new AppBootstrapError(errorMessage, errorCode)
      }

      await deviceManagementService.initDeviceManagement({
        appName: 'Terminal NxG',
        appRunUuid,
        autoSetDeviceAccessToken: isOperatorLoggedIn
      })

      if (!appData.value) {
        logService.error(`${LOG_PREFIX} App data is not defined`, {
          appRunUuid,
          code: 'SBO_BOOT_APP_DATA_UNDEFINED'
        })
        return
      }

      initBackOfficeAcceptors()

      settingsStore.getStoredSettings()
      const lang = settingsStore.resolveBootstrapLang(appData.value.appConfig.data.defaultLanguage)

      sevenIgamingPlatformConfig.language = ref(lang)

      await deviceManagementPrintService.init(printConfig).catch((error) => {
        logService.error(`${LOG_PREFIX} Failed to initialize DM print service.`, {
          code: 'SBO_BOOT_DM_PRINT_SERVICE_INIT_ERROR',
          ...errorParser.parseUpstream(error)
        })
      })

      if (import.meta.env.PROD) {
        httpLogPlugin.setAdditionalLogData({
          app_run_uuid: appRunUuid,
          tenant_id: appData.value.betshop.companyUuid,
          tenant_name: appData.value.betshop.companyName,
          device_id: appData.value.device.uuid,
          betshop_id: appData.value.betshop.uuid
        })
      }

      const cmsConfig = await cmsService
        .initCmsService('ssbt-bo', {
          activeGames: appData.value.appConfig.data.appConfig.products,
          tenantId: appData.value.betshop.companyUuid,
          deviceUuid: appData.value.device.uuid,
          lang
        })
        .catch((error) => {
          logService.error(`${LOG_PREFIX} Failed to initialize CMS service.`, {
            code: 'SBO_BOOT_CMS_SERVICE_INIT_ERROR',
            ...errorParser.parseUpstream(error)
          })
          throw error
        })

      useCmsConfigStore().setCmsConfig(cmsConfig)
      applyDeviceSettingsFromBootstrap()

      if (!operatorData.value && isOperatorLoggedIn.value) {
        logService.info(
          `${LOG_PREFIX} Operator is logged in but operator data is not available. Attempting to fetch operator data.`,
          {
            code: 'SBO_BOOT_OPERATOR_DATA_FETCH_ATTEMPT'
          }
        )
        startOperatorAccessTokenAutoRefresh(() => {
          router.replace({ name: 'login' })
        })

        await getOperatorData().catch((error) => {
          logService.error(
            `${LOG_PREFIX} Failed to get operator data. Redirecting operator to login page`,
            {
              code: 'SBO_BOOT_OPERATOR_DATA_FETCH_ERROR',
              ...errorParser.parseUpstream(error)
            }
          )
          router.replace({ name: 'login' })

          throw error
        })

        await setDeviceAccessToken().catch((error) => {
          logService.warn(`${LOG_PREFIX} Failed to set device access token.`, {
            code: 'SBO_BOOT_SET_DEVICE_ACCESS_TOKEN_ERROR',
            ...errorParser.parseUpstream(error)
          })
        })

        await initTokenServices()
        bootGames()
      } else {
        bootGames()
      }

      settingsStore.loadLanguages()
      addScanListener()
    } catch (err) {
      logService.error(`${LOG_PREFIX} App bootstrap failed.`, {
        code: 'SBO_BOOT_APP_ERROR',
        ...errorParser.parseUpstream(err)
      })
    } finally {
      isAppReady.value = true
      closeSpinner()
    }
  }

  const handleDeviceAccessTokenReceived = (deviceAccessToken: string) => {
    try {
      tokenPayloadStore.setToken(deviceAccessToken)
      sevenIgamingPlatformConfig.token = deviceAccessToken
    } catch (error) {
      logService.error(`${LOG_PREFIX} Failed to handle device access token.`, {
        code: 'SBO_BOOT_DEVICE_ACCESS_TOKEN_HANDLE_ERROR',
        ...errorParser.parseUpstream(error)
      })
    }
  }

  const waitForAppReady = (): Promise<void> => {
    if (isAppReady.value) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      const stop = watch(isAppReady, (ready) => {
        if (ready) {
          stop()
          resolve()
        }
      })
    })
  }

  return {
    isAppReady: readonly(isAppReady),
    startApp,
    initTokenServices,
    handleDeviceAccessTokenReceived,
    waitForAppReady
  }
}
